import { forwardRef, useState } from "react";
import { Eye, EyeOff, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { FOCUS_RING, CONTROL_SIZE, DISABLED } from "./tokens";
import { useFieldContext } from "./Field";

/* Base compartilhada por input, select e textarea. */
const controlBase = cn(
  "w-full rounded-lg border bg-white px-3 text-slate-900 placeholder:text-slate-400",
  "transition-colors",
  FOCUS_RING,
  DISABLED,
);

const borderFor = (invalid) =>
  invalid
    ? "border-red-300 hover:border-red-400"
    : "border-slate-200 hover:border-slate-300";

/** Junta o estado do Field (id, aria-describedby, erro) com as props locais. */
function useControlProps({ id, invalid, size = "md", mono, className, ...rest }) {
  const field = useFieldContext();
  const isInvalid = invalid ?? field?.invalid ?? false;
  return {
    id: id || field?.id,
    "aria-describedby": rest["aria-describedby"] || field?.describedBy,
    "aria-invalid": isInvalid || undefined,
    required: rest.required ?? field?.required,
    "data-ring": "",
    className: cn(controlBase, CONTROL_SIZE[size], borderFor(isInvalid), mono && "font-mono", className),
    ...rest,
  };
}

export const Input = forwardRef(function Input(props, ref) {
  return <input ref={ref} {...useControlProps(props)} />;
});

export const Select = forwardRef(function Select({ children, ...props }, ref) {
  // `pr-8` abre espaço para a seta nativa; sem isso o texto encosta nela.
  const p = useControlProps(props);
  return (
    <select ref={ref} {...p} className={cn(p.className, "pr-8 cursor-pointer")}>
      {children}
    </select>
  );
});

export const Textarea = forwardRef(function Textarea({ rows = 3, ...props }, ref) {
  const p = useControlProps({ ...props, size: undefined });
  // Textarea não tem altura fixa: troca a altura do CONTROL_SIZE por padding vertical.
  // O `text-base` abaixo de `md` segue a mesma regra do CONTROL_SIZE (zoom no iOS).
  return <textarea ref={ref} rows={rows} {...p} className={cn(p.className, "h-auto py-2 text-base md:text-sm leading-relaxed resize-y")} />;
});

/**
 * Senha com botão de mostrar/ocultar.
 *
 * No celular é onde o login trava: digitar senha às cegas num teclado de toque,
 * com autocorreção por perto, faz errar — e o erro só aparece depois de enviar.
 * O botão fica dentro do campo e é alcançável por teclado (não é `tabIndex=-1`),
 * anunciando o estado por `aria-pressed`, para quem usa leitor de tela saber se
 * a senha está à mostra.
 */
export const PasswordInput = forwardRef(function PasswordInput(props, ref) {
  const [visible, setVisible] = useState(false);
  // `pr-11` abre espaço para o botão; sem isso a senha passa por baixo do olho.
  const p = useControlProps({ ...props, type: visible ? "text" : "password" });
  return (
    <div className="relative">
      <input ref={ref} {...p} className={cn(p.className, "pr-11")} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
        aria-pressed={visible}
        className={cn(
          "absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-400",
          "hover:text-slate-700 hover:bg-slate-100 transition-colors", FOCUS_RING,
        )}
        data-ring=""
      >
        {visible ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
      </button>
    </div>
  );
});

/**
 * Campo de busca das listagens.
 *
 * Substitui o padrão `<div class="flex items-center border..."><Search/><input
 * class="outline-none"/></div>` repetido em 9 telas — que matava o outline sem
 * repor nada e deixava o ícone fora da área clicável do label.
 */
export const SearchInput = forwardRef(function SearchInput(
  { value, onChange, onClear, placeholder = "Buscar...", size = "md", className, width = "w-64", ...rest },
  ref,
) {
  const field = useFieldContext();
  const showClear = !!value && (onClear || onChange);
  return (
    <div className={cn("relative", width, className)}>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden />
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        id={rest.id || field?.id}
        aria-label={rest["aria-label"] || (field ? undefined : placeholder)}
        data-ring=""
        className={cn(
          controlBase, CONTROL_SIZE[size], borderFor(false),
          "pl-9", showClear && "pr-9",
          // Esconde o "x" nativo do type=search (visual inconsistente entre navegadores).
          "[&::-webkit-search-cancel-button]:appearance-none",
        )}
        {...rest}
      />
      {showClear && (
        <button
          type="button"
          onClick={() => (onClear ? onClear() : onChange({ target: { value: "" } }))}
          aria-label="Limpar busca"
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400",
            "hover:text-slate-700 hover:bg-slate-100 transition-colors", FOCUS_RING,
          )}
          data-ring=""
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
});

/** Checkbox com rótulo clicável e alvo de toque adequado. */
export const Checkbox = forwardRef(function Checkbox({ label, className, ...rest }, ref) {
  return (
    <label className={cn("inline-flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer py-2 select-none", className)}>
      <input
        ref={ref}
        type="checkbox"
        data-ring=""
        className={cn(
          "h-4 w-4 rounded border-slate-300 text-brand-600 cursor-pointer",
          "accent-brand-600", FOCUS_RING,
        )}
        {...rest}
      />
      {label}
    </label>
  );
});

export default Input;
